'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import PropTypes from 'prop-types';
import styled from 'styled-components';
import classNames from 'classnames';
import { OLAF } from 'HubStyleTokens/colors';
import { DEVICE_CLASSES, LAYOUT_CLASSES } from './PageConstants';
import UIAbstractPageGridContent from './UIAbstractPageGridContent';
import UIAbstractPageSection from './UIAbstractPageSection';
import UIPage from './UIPage';
import UIHeader from '../layout/UIHeader';
import * as CustomRenderer from '../utils/propTypes/customRenderer';
import { NAV_BAR_HEIGHT } from 'HubStyleTokens/sizes';
import { toPx } from '../utils/Styles';

function mainWrapperBackgroundColor(color) {
  return {
    backgroundColor: color
  };
}

function getBodyClassNames(bodyClassName, pageLayout) {
  return classNames('space-sword', bodyClassName, pageLayout === 'max-width' && 'space-sword--max-width');
}

const StyledPage = styled(props => {
  const rest = _objectWithoutPropertiesLoose(props, ["navHeight"]);

  return /*#__PURE__*/_jsx(UIPage, Object.assign({}, rest));
}).withConfig({
  displayName: "UIAbstractPageTemplate__StyledPage",
  componentId: "sc-19iwayw-0"
})(["display:flex;flex-direction:column;flex-grow:1;background-color:", ";min-height:calc( 100vh - ", " );"], OLAF, ({
  navHeight
}) => typeof navHeight === 'number' && navHeight >= 0 ? toPx(navHeight) : NAV_BAR_HEIGHT);

function UIAbstractPageTemplate(_ref) {
  let {
    asDevice,
    bodyClassName,
    children,
    className,
    contentAreaFlush,
    headerComponent,
    horizontalDivider,
    mainClassName,
    MainSection,
    mainSectionBackgroundColor,
    mainSectionStyle,
    narrowLayout,
    navHeight,
    pageBackgroundColor,
    pageLayout,
    pageMaxWidth,
    pageStyle,
    sidebarAreaFlush,
    sidebarComponent,
    sidebarStyle,
    stickySidebar,
    tabs,
    title
  } = _ref,
      rest = _objectWithoutPropertiesLoose(_ref, ["asDevice", "bodyClassName", "children", "className", "contentAreaFlush", "headerComponent", "horizontalDivider", "mainClassName", "MainSection", "mainSectionBackgroundColor", "mainSectionStyle", "narrowLayout", "navHeight", "pageBackgroundColor", "pageLayout", "pageMaxWidth", "pageStyle", "sidebarAreaFlush", "sidebarComponent", "sidebarStyle", "stickySidebar", "tabs", "title"]);

  const headerUse = tabs ? 'condensed' : undefined;
  const headerWrapperClassNames = 'private-template__section--header';
  const mainWrapperClassNames = classNames('private-template__section--stretch', horizontalDivider && 'private-template__section--divided', narrowLayout && 'private-template--narrow');
  const pageClassName = classNames('private-template', DEVICE_CLASSES[asDevice], LAYOUT_CLASSES[pageLayout], className);
  const sectionClassNames = contentAreaFlush ? 'private-template__section--flush' : 'private-template__section--spaced-vertical';

  const templateBackgroundColor = color => {
    return {
      backgroundColor: color
    };
  };

  const templateMaxWidth = width => {
    if (!width) return null;
    return {
      maxWidth: width
    };
  };

  const sidebarProps = {
    mainClassName,
    sidebarAreaFlush,
    sidebarComponent,
    stickySidebar
  };

  if (MainSection === UIAbstractPageTemplate.defaultProps.MainSection) {
    sidebarProps.sidebarStyle = sidebarStyle;
  }

  return /*#__PURE__*/_jsx("div", {
    className: "private-ie11-flex-wrapper",
    children: /*#__PURE__*/_jsxs(StyledPage, Object.assign({}, rest, {
      bodyClassName: getBodyClassNames(bodyClassName, pageLayout),
      className: pageClassName,
      navHeight: navHeight,
      style: Object.assign({}, templateBackgroundColor(pageBackgroundColor), {}, pageStyle),
      children: [/*#__PURE__*/_jsxs(UIAbstractPageSection, {
        className: headerWrapperClassNames,
        style: Object.assign({}, templateMaxWidth(pageMaxWidth)),
        children: [CustomRenderer.render(headerComponent, {
          title,
          use: headerUse
        }), tabs]
      }), /*#__PURE__*/_jsx("div", {
        className: mainWrapperClassNames,
        style: Object.assign({}, mainWrapperBackgroundColor(mainSectionBackgroundColor), {}, mainSectionStyle),
        children: /*#__PURE__*/_jsx(UIAbstractPageSection, {
          className: sectionClassNames,
          style: Object.assign({}, templateMaxWidth(pageMaxWidth)),
          children: /*#__PURE__*/_jsx(MainSection, Object.assign({}, sidebarProps, {
            style: Object.assign({}, mainWrapperBackgroundColor(mainSectionBackgroundColor), {}, mainSectionStyle),
            children: children
          }))
        })
      })]
    }))
  });
}

UIAbstractPageTemplate.propTypes = {
  asDevice: PropTypes.oneOf(['mobile', 'tablet', 'desktop']),
  bodyClassName: PropTypes.string,
  children: PropTypes.node.isRequired,
  contentAreaFlush: PropTypes.bool.isRequired,
  horizontalDivider: PropTypes.bool.isRequired,
  headerComponent: CustomRenderer.propType,

  /**
   * A custom className to pass to the `<main />` container.
   */
  mainClassName: PropTypes.string,
  MainSection: PropTypes.elementType,
  mainSectionBackgroundColor: PropTypes.string,
  mainSectionComponent: CustomRenderer.propType,
  mainSectionStyle: PropTypes.object,
  narrowLayout: PropTypes.bool.isRequired,
  navHeight: PropTypes.number,
  pageBackgroundColor: PropTypes.string,
  pageLayout: PropTypes.oneOf(Object.keys(LAYOUT_CLASSES)),
  pageMaxWidth: PropTypes.number,
  pageStyle: PropTypes.object,
  sidebarComponent: CustomRenderer.propType,
  sidebarAreaFlush: PropTypes.bool,
  sidebarStyle: PropTypes.object,
  stickySidebar: PropTypes.bool,
  tabs: PropTypes.node,
  title: PropTypes.node
};
UIAbstractPageTemplate.defaultProps = {
  contentAreaFlush: false,
  headerComponent: UIHeader,
  horizontalDivider: false,
  MainSection: UIAbstractPageGridContent,
  narrowLayout: false,
  pageLayout: 'max-width',
  sidebarAreaFlush: false,
  stickySidebar: false
};
UIAbstractPageTemplate.displayName = 'UIAbstractPageTemplate';
export default UIAbstractPageTemplate;